import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMood } from '../../contexts/MoodContext';
import { getMood, greetingPrefix, MoodId } from './moods';
import MoodCheckIn from './MoodCheckIn';
import MoodInsights from './MoodInsights';
import MoodSettings from './MoodSettings';
import MoodActivity from './MoodActivity';
import AmbientBackground from './visual/AmbientBackground';
import FloatingParticleLayer from './visual/FloatingParticleLayer';
import AnimatedMoodIllustration from './visual/AnimatedMoodIllustration';
import { usePointerParallax } from './visual/hooks';
import { useRipples, RippleLayer } from './visual/worlds/useRipples';
import './MoodMate.css';

/**
 * MoodMatePage — the personalized, mood-adaptive dashboard.
 *
 * Everything on it is optional and reversible. When no mood is set for the
 * session (first visit or after skipping) it shows the gentle picker inline
 * instead of a dashboard. Once a mood is chosen it renders a greeting, sample
 * suggestions, one functional wellness activity, a private journal, an honest
 * weekly summary and the settings — all themed by the mood's accent, all over
 * the existing brand surface.
 */

export const MoodMatePage: React.FC = () => {
  const { user } = useAuth();
  const { sessionMood, chooseMood } = useMood();
  const navigate = useNavigate();
  const [picking, setPicking] = useState(false);
  const [pickPreview, setPickPreview] = useState<MoodId | null>(null);
  const parallaxRef = usePointerParallax<HTMLDivElement>();
  const heroRipples = useRipples();

  const mood = getMood(sessionMood);
  const previewMood = getMood(pickPreview);
  const firstName = user?.name?.trim().split(/\s+/)[0] || '';

  const handlePick = (m: MoodId) => {
    chooseMood(m);
    setPicking(false);
  };

  // No mood for this session, or the user tapped "Change mood" → show the picker,
  // over the same immersive animated environment as the dashboard and gate.
  if (!mood || picking) {
    return (
      <div
        ref={parallaxRef}
        className="mm-page mm-page--immersive"
        style={
          previewMood
            ? { ['--mood-accent' as string]: previewMood.accent, ['--mood-soft' as string]: previewMood.soft }
            : undefined
        }
      >
        <AmbientBackground />
        <FloatingParticleLayer count={12} />
        <div className="mm-page__inner mm-page__inner--center">
          <div className="mm-panel mm-panel--picker mm-glass">
            <MoodCheckIn
              onContinue={handlePick}
              onSkip={picking ? () => setPicking(false) : undefined}
              onSelect={setPickPreview}
              initialMood={sessionMood}
              title={picking ? 'Switch up your vibe' : "How's your vibe today?"}
              continueLabel={picking ? 'Update mood' : 'Continue'}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={parallaxRef}
      className="mm-page mm-page--immersive"
      style={{ ['--mood-accent' as string]: mood.accent, ['--mood-soft' as string]: mood.soft }}
    >
      <AmbientBackground />
      <FloatingParticleLayer count={10} />
      <div className="mm-page__inner">
        {/* A. Greeting + today's mood visualization */}
        <header className="mm-hero mm-enter">
          <div className="mm-hero__text">
            <h1 className="mm-hero__greeting">
              {greetingPrefix()}<span className="mm-gradient-text">{firstName ? `, ${firstName}` : ''}</span>.
            </h1>
            <p className="mm-hero__line">{mood.greeting}</p>
            <p className="mm-hero__affirm">“{mood.affirmation}”</p>
            <div className="mm-hero__mood">
              <span className="mm-hero__mood-label">Today you feel {mood.label.toLowerCase()}</span>
              <button type="button" className="mm-link-btn" onClick={() => setPicking(true)}>Change mood</button>
            </div>
            <button type="button" className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => navigate('/my-information/mood-journal')}>
              Mood journal →
            </button>
          </div>
          <button
            type="button"
            className="mm-hero__viz"
            aria-label={`Play with your ${mood.label.toLowerCase()} mood`}
            onClick={(e) => heroRipples.spawnAt(e.currentTarget, e.clientX, e.clientY)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const r = e.currentTarget.getBoundingClientRect();
                heroRipples.spawnAt(e.currentTarget, r.left + r.width / 2, r.top + r.height / 2);
              }
            }}
          >
            <span className="mm-hero__glow" aria-hidden="true" />
            <AnimatedMoodIllustration mood={mood.id} size="lg" />
            <RippleLayer ripples={heroRipples.ripples} />
          </button>
        </header>

        <div className="mm-grid">
          {/* B. Recommended for you */}
          <section className="mm-panel mm-span-2">
            <h2 className="mm-panel__title">Recommended for you</h2>
            <div className="mm-recs">
              {mood.suggestions.map((s, i) => (
                <div className="mm-rec mm-enter" style={{ ['--mm-i' as string]: i }} key={s.title}>
                  <span className="mm-rec__icon" aria-hidden="true">{s.icon}</span>
                  <div className="mm-rec__body">
                    <span className="mm-rec__title">{s.title}</span>
                    <span className="mm-rec__desc">{s.desc}</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="mm-panel__foot">Suggestions only — nothing here is connected to an external service.</p>
          </section>

          {/* C. A little playful thing to do — optional, nothing to complete */}
          <section className="mm-panel mm-panel--activity">
            <h2 className="mm-panel__title">{mood.activity.title}</h2>
            <p className="mm-panel__sub">{mood.activity.desc}</p>
            <div className="mm-activity"><MoodActivity type={mood.activity.type} /></div>
          </section>

          {/* E. A gentle look at your week (no targets, no judgement) */}
          <section className="mm-panel">
            <h2 className="mm-panel__title">Your week so far</h2>
            <MoodInsights />
          </section>

          {/* F. Settings */}
          <section className="mm-panel mm-span-2">
            <h2 className="mm-panel__title">Your preferences</h2>
            <MoodSettings />
          </section>
        </div>
      </div>
    </div>
  );
};

export default MoodMatePage;
